import { AIResultType } from "../@types/enums";
import bearerAxios from "../helpers/bearerAxios";
import { SavedAIDesign } from "../store/oneButtonStore";
import { logger } from "./loggingService";

const GEMINI_BASE = "https://generativelanguage.googleapis.com/v1beta";

//export const GEMINI_MODEL_ID = "gemini-3.1-flash-lite-preview";
export const GEMINI_MODEL_ID = "gemini-2.5-flash-lite";

/** Retries after the first failed attempt (4 total attempts). */
const GEMINI_TRANSIENT_RETRY_COUNT = 3;
const GEMINI_TRANSIENT_RETRY_DELAY_MS = 2500;

function isRetryableGeminiHttpStatus(status: number): boolean {
    return status === 503 || status === 429 || status === 502 || status === 504;
}

function sleepMs(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function postGeminiGenerateContent(url: string, body: Record<string, unknown>): Promise<Response> {
    let lastError: Error | null = null;
    const maxAttempts = GEMINI_TRANSIENT_RETRY_COUNT + 1;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });

        if (res.ok) {
            return res;
        }

        const errText = await res.text();
        const error = new Error(formatGeminiApiErrorMessage(res.status, errText));
        const canRetry = isRetryableGeminiHttpStatus(res.status) && attempt < maxAttempts - 1;
        if (!canRetry) {
            throw error;
        }

        lastError = error;
        logger.warn(
            `Gemini request failed (${res.status}); retrying in ${GEMINI_TRANSIENT_RETRY_DELAY_MS}ms ` +
                `(attempt ${attempt + 1}/${maxAttempts})`,
        );
        await sleepMs(GEMINI_TRANSIENT_RETRY_DELAY_MS);
    }

    throw lastError ?? new Error("AI request failed after retries.");
}

/**
 * Map provider REST error bodies to short, actionable UI copy (Run AI, utility bill extract, etc.).
 */
function formatGeminiApiErrorMessage(status: number, errText: string): string {
    const raw = errText.trim();
    let nestedMessage = "";
    try {
        const j = JSON.parse(raw) as { error?: { message?: string } };
        if (typeof j?.error?.message === "string") nestedMessage = j.error.message;
    } catch {
        /* not JSON */
    }
    const haystack = `${raw} ${nestedMessage}`.toLowerCase();
    if (
        status === 400 &&
        (haystack.includes("input token count exceeds") ||
            haystack.includes("maximum number of tokens allowed") ||
            haystack.includes("1048576"))
    ) {
        return (
            "This request exceeded the maximum payload size allowed. " +
            "When running AI designs, try fewer layout or tree-cutting scenarios or reduce site detail sent to the model, then try again."
        );
    }
    return `AI request failed (${status}): ${raw}`;
}

export async function callGemini(
    prompt: string,
    modelId: string,
    apiKey: string,
    options?: { responseMimeType?: string }
): Promise<string> {
    const url = `${GEMINI_BASE}/models/${modelId}:generateContent?key=${encodeURIComponent(apiKey)}`;
    const res = await postGeminiGenerateContent(url, {
        contents: [{ role: "user", parts: [{ text: prompt }] }],
        generationConfig: {
            ...(options?.responseMimeType && { responseMimeType: options.responseMimeType }),
            // Use deterministic sampling for consistent layout and tree-cutting results.
            temperature: 0,
            maxOutputTokens: 16384,
        },
    });
    const data = await res.json();
    const text = data?.candidates?.[0]?.content?.parts?.[0]?.text?.trim();
    if (!text) {
        throw new Error("Empty or invalid response from the AI service.");
    }
    return text;
}

export type GeminiChatTurn = { role: "user" | "model"; text: string };

/**
 * Multi-turn chat with optional system instruction (Gemini generateContent).
 * Roles must alternate user / model as in the Gemini API.
 */
export async function callGeminiChat(
    apiKey: string,
    systemInstruction: string,
    history: GeminiChatTurn[],
    userMessage: string,
    options?: { modelId?: string; temperature?: number; maxOutputTokens?: number }
): Promise<string> {
    const modelId = options?.modelId ?? GEMINI_MODEL_ID;
    const url = `${GEMINI_BASE}/models/${modelId}:generateContent?key=${encodeURIComponent(apiKey)}`;
    const contents = [
        ...history.map((t) => ({
            role: t.role,
            parts: [{ text: t.text }],
        })),
        { role: "user", parts: [{ text: userMessage }] },
    ];
    const res = await postGeminiGenerateContent(url, {
        systemInstruction: { parts: [{ text: systemInstruction }] },
        contents,
        generationConfig: {
            temperature: options?.temperature ?? 0.35,
            maxOutputTokens: options?.maxOutputTokens ?? 8192,
        },
    });
    const data = await res.json();
    const text = data?.candidates?.[0]?.content?.parts?.[0]?.text?.trim();
    if (!text) {
        throw new Error("Empty or invalid response from the AI service.");
    }
    return text;
}

/** Simple ping prompt to verify Gemini is reachable. Returns the model's reply. */
export async function pingGemini(apiKey: string): Promise<string> {
    const prompt = "Reply with exactly: PONG. Then add one short sentence that you are ready to help.";
    return callGemini(prompt, GEMINI_MODEL_ID, apiKey);
}

/** Result of extracting utility bill fields from an uploaded document via Gemini. Omit fields not found in the file. */
export interface ExtractedUtilityBillFields {
    shutoffNotice?: string;
    meterId?: string;
    accountNumber?: string;
    dateOfBill?: string;
    monthlyUsage?: {month: number, value?: number, amount?: number}[];
    annualUsage?: number;
    CompanyName?: string;
}

const UTILITY_BILL_EXTRACT_PROMPT = `You are extracting data from a utility bill document (PDF or image). Extract only the following fields if they are clearly present in the document. If a field is not present or cannot be determined, omit it from your response.

Fields to extract:
1. shutoffNotice: Whether there is a shutoff notice or disconnection notice. Reply with exactly "Yes" or "No" only. Omit if not mentioned.
2. meterId: The meter ID or meter number. Omit if not found.
3. accountNumber: The account number. Omit if not found.
4. dateOfBill: The bill date or statement date in ISO format YYYY-MM-DD. Omit if not found.

Reply with a JSON object containing only the fields you found. Example: {"shutoffNotice":"No","meterId":"12345","accountNumber":"ACC-67890","dateOfBill":"2024-01-15"}
Do not include any field that is not clearly present in the document. No commentary, only the JSON object.`;

/**
 * Extract utility bill fields (shutoff notice, meter ID, account number, date of bill) from an uploaded file using Gemini.
 * Supports PDF and image MIME types. Returns only fields that were found; ignores missing data.
 */
export async function extractUtilityBillFields(
    file: File,
): Promise<ExtractedUtilityBillFields> {
    const mime = file.type || "";
    const supported =
        mime === "application/pdf" ||
        mime.startsWith("image/");
    if (!supported) {
        return {};
    }
    const buffer = await file.arrayBuffer();
    const base64 = btoa(
        new Uint8Array(buffer).reduce((acc, byte) => acc + String.fromCharCode(byte), "")
    );
    const apiKey = import.meta.env?.VITE_GEMINI_API_KEY as string;
    const url = `${GEMINI_BASE}/models/${GEMINI_MODEL_ID}:generateContent?key=${encodeURIComponent(apiKey)}`;
    const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            contents: [
                {
                    role: "user",
                    parts: [
                        { text: UTILITY_BILL_EXTRACT_PROMPT },
                        { inlineData: { mimeType: mime || "application/pdf", data: base64 } },
                    ],
                },
            ],
            generationConfig: {
                responseMimeType: "application/json",
                temperature: 0.2,
                maxOutputTokens: 2048,
            },
        }),
    });
    if (!res.ok) {
        const errText = await res.text();
        throw new Error(formatGeminiApiErrorMessage(res.status, errText));
    }
    const data = await res.json();
    const text = data?.candidates?.[0]?.content?.parts?.[0]?.text?.trim();
    if (!text) return {};
    try {
        const parsed = JSON.parse(text) as Record<string, unknown>;
        const out: ExtractedUtilityBillFields = {};
        if (typeof parsed.shutoffNotice === "string" && parsed.shutoffNotice.trim()) {
            out.shutoffNotice = parsed.shutoffNotice.trim();
        }
        if (typeof parsed.meterId === "string" && parsed.meterId.trim()) {
            out.meterId = parsed.meterId.trim();
        }
        if (typeof parsed.accountNumber === "string" && parsed.accountNumber.trim()) {
            out.accountNumber = parsed.accountNumber.trim();
        }
        if (typeof parsed.dateOfBill === "string" && parsed.dateOfBill.trim()) {
            out.dateOfBill = parsed.dateOfBill.trim();
        }
        return out;
    } catch {
        return {};
    }
}

/** Result of extracting utility rate(s) from a bill via Gemini. Use allInRate for "Current All In Utility Rate" when present. */
export interface ExtractedUtilityRates {
    /** Effective all-in rate ($/kWh or similar) when the bill shows a blended or total rate */
    allInRate?: number;
    /** Rate per kWh if shown separately */
    ratePerKwh?: number;
    /** Total amount due or bill total if useful for display */
    totalAmount?: number;
}

const UTILITY_RATE_EXTRACT_PROMPT = `You are extracting utility rate information from a utility bill document (PDF or image). Extract only the following numeric values if they are clearly present. Use decimal numbers (e.g. 0.15 for 15 cents). If a value is not present or cannot be determined, omit it.

Fields to extract:
1. allInRate: The effective all-in rate, blended rate, or total rate per kWh in dollars (e.g. 0.162 for 16.2 cents/kWh). Look for "rate", "price per kWh", "effective rate", "blended rate", or similar.
2. ratePerKwh: The energy charge or rate per kWh in dollars if shown separately from other charges.
3. totalAmount: The total amount due or current charges in dollars (optional).

Reply with a JSON object containing only the fields you found. Example: {"allInRate":0.162} or {"ratePerKwh":0.12,"totalAmount":185.50}
No commentary, only the JSON object.`;

/**
 * Parse and fetch utility rate from an uploaded bill using Gemini. Use when the user uploads an image or document in the Bill detail section.
 */
export async function extractUtilityRatesFromBill(file: File): Promise<ExtractedUtilityRates> {
    const mime = file.type || "";
    const supported = mime === "application/pdf" || mime.startsWith("image/");
    if (!supported) return {};
    const buffer = await file.arrayBuffer();
    const base64 = btoa(
        new Uint8Array(buffer).reduce((acc, byte) => acc + String.fromCharCode(byte), "")
    );
    const apiKey = import.meta.env?.VITE_GEMINI_API_KEY as string;
    if (!apiKey) return {};
    const url = `${GEMINI_BASE}/models/${GEMINI_MODEL_ID}:generateContent?key=${encodeURIComponent(apiKey)}`;
    const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            contents: [
                {
                    role: "user",
                    parts: [
                        { text: UTILITY_RATE_EXTRACT_PROMPT },
                        { inlineData: { mimeType: mime || "application/pdf", data: base64 } },
                    ],
                },
            ],
            generationConfig: {
                responseMimeType: "application/json",
                temperature: 0.2,
                maxOutputTokens: 1024,
            },
        }),
    });
    if (!res.ok) return {};
    const data = await res.json();
    const text = data?.candidates?.[0]?.content?.parts?.[0]?.text?.trim();
    if (!text) return {};
    try {
        const parsed = JSON.parse(text) as Record<string, unknown>;
        const out: ExtractedUtilityRates = {};
        if (typeof parsed.allInRate === "number" && Number.isFinite(parsed.allInRate)) {
            out.allInRate = parsed.allInRate;
        }
        if (typeof parsed.ratePerKwh === "number" && Number.isFinite(parsed.ratePerKwh)) {
            out.ratePerKwh = parsed.ratePerKwh;
        }
        if (typeof parsed.totalAmount === "number" && Number.isFinite(parsed.totalAmount)) {
            out.totalAmount = parsed.totalAmount;
        }
        return out;
    } catch {
        return {};
    }
}

export async function utilityBillReader(
    file: File,
): Promise<ExtractedUtilityBillFields> {
    const mime = file.type || "";
    if (mime !== "application/pdf" && !mime.includes("image/")) {
        throw new Error("Unsupported file type");
    }

    let formData: FormData | any = new FormData();

    // Send the same file for both fields used in the curl; backend can read either.
    formData.append("bill", file);
    // formData.append("UtilityFile", file);
    // formData.append("UtilityName", "National Grid");

    const res = await bearerAxios.post(import.meta.env.VITE_RPABOX_URL + import.meta.env.VITE_UTILITY_EBILL_DATA_READER!,
        formData, {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    });
    if (res.status !== 200) {
        throw new Error(`Utility bill reader error ${res.status}`);
    }

    const data = res.data.data;

    function convertDateFormat(dateStr: string) {
        // Parse the input string into a Date object
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) {
            return "";
        }
      
        // Extract components
        const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
      
        // Return in MM-DD-YYYY format
        return `${month}-${day}-${year}`;
      }
      
    
    const out: ExtractedUtilityBillFields = {

        meterId: data.MeterNumber?.[0] ?? "",
        accountNumber: data.AccountNumber?.[0] ?? "",
        dateOfBill: convertDateFormat(data.StatementDate?.[0] ?? ""),
        shutoffNotice: data.ShutoffNotice?.[0] ?? "",
        monthlyUsage: data.MonthlyUsage ?? [],
        annualUsage: data.AnnualUsage?.[0] ?? 0,
        CompanyName: data.Company?.[0] ?? "",
    };

    return out;
}

/** Shade point with weight for AI input */
export interface ModuleFillingShadePointInput {
    index: number;
    ptHashVal: number;
    x: number;
    y: number;
}

/** Input for AI module-fill recommendation: scenarios with shade weights and tree multipliers */
export interface ModuleFillingScenarioInput {
    treeLabel: string | null;
    treeId: string | null;
    /** When length > 1, scenario is multi-tree cut with actual production */
    treeIds?: string[] | null;
    cutCost: number | null;
    shadeWeights: Array<{
        roofFaceId: string;
        roofNum: number;
        solarAccess: number;
        roofMultiplier: number;
        /** Number of modules on this roof in the current layout */
        moduleCount?: number;
        /** Actual production (W) for this roof from shade calculation when available */
        actualProduction?: number;
        shadePoints?: ModuleFillingShadePointInput[];
        /**
         * Optional access-multipliers detail for this roof (from savedAccessMultipliersForSolarRoofs / savedRoofWiseAccessMultiplierDataArray).
         * This lets the model see how production varies with solar-access bands.
         */
        accessMultipliers?: {
            azimuth: number;
            tilt: number;
            multipliers: Array<{
                access: number;
                multiplier: number;
                monthlyMultipliers: Array<{
                    month: number;
                    multiplier: number;
                }>;
            }>;
        };
    }>;
    /** Trees with treeImpactMulti > 0 only; site trees not listed are treated as 0 impact. */
    treeMultipliers: Array<{ treeId: string; treeLabel: string; treeImpactMulti?: number }>;
    systemMultiplier: number;
    actualWattage: number;
    /**
     * Optional additional drawing / rule context for this scenario.
     * These fields are gradually populated from the viewer/store and give the model
     * more information about how the layout was created and which areas are allowed.
     */
    ahjRulesSummary?: string;
    /** Encoded setback regions (stored / batch export; omitted from Gemini module-filling payload). */
    setbackSummary?: string;
    /** Encoded pathway regions (stored / batch export; omitted from Gemini module-filling payload). */
    pathwaySummary?: string;
    /** Roof/obstruction sketch text from the viewer (stored / batch export; omitted from Gemini module-filling payload). */
    drawingSummary?: string;
    /** Module type and key electrical / mechanical metadata used for this layout */
    moduleTypeSummary?: string;
    /** High-level summary of tree geometry / species / height etc, when available */
    treeMetaSummary?: string;
}

/** Limits to stay under Gemini input token budgets (very large sites × many scenarios otherwise exceed ~1M tokens). */
const MAX_SHADE_POINTS_PER_ROOF_IN_PROMPT = 24;
const MAX_LAYOUT_VARIANTS_SUMMARY_CHARS = 12000;
const MAX_SCENARIOS_IN_PROMPT = 32;

function truncateText(value: string | undefined | null, maxChars: number, label: string): string | undefined {
    if (value == null || value === "") return undefined;
    if (value.length <= maxChars) return value;
    return `${value.slice(0, maxChars)}\n...[${label} truncated ${value.length - maxChars} chars]`;
}

/**
 * Keep a small, representative set of shade points (high-access, low-access, plus index spread) for the base scenario only.
 */
function compactShadePointsForPrompt(
    points: ModuleFillingShadePointInput[] | undefined,
    maxPerRoof: number,
): ModuleFillingShadePointInput[] | undefined {
    if (!points?.length) return undefined;
    if (points.length <= maxPerRoof) return points;

    const byIndex = new Map<number, ModuleFillingShadePointInput>();
    const sortedHigh = [...points].sort((a, b) => b.ptHashVal - a.ptHashVal);
    const takeTop = Math.min(14, maxPerRoof);
    sortedHigh.slice(0, takeTop).forEach((p) => byIndex.set(p.index, p));

    const sortedLow = [...points].sort((a, b) => a.ptHashVal - b.ptHashVal);
    const takeLow = Math.min(6, maxPerRoof - byIndex.size);
    sortedLow.slice(0, takeLow).forEach((p) => byIndex.set(p.index, p));

    const step = Math.max(1, Math.ceil(points.length / Math.max(1, maxPerRoof - byIndex.size)));
    for (let i = 0; i < points.length && byIndex.size < maxPerRoof; i += step) {
        const p = points[i];
        byIndex.set(p.index, p);
    }

    return Array.from(byIndex.values()).sort((a, b) => a.index - b.index);
}

/** Drop monthly arrays from access multipliers (large); annual access→multiplier bands are enough for reasoning. */
function compactAccessMultipliersForPrompt(
    am:
        | NonNullable<ModuleFillingScenarioInput["shadeWeights"][number]>["accessMultipliers"]
        | undefined,
):
    | NonNullable<ModuleFillingScenarioInput["shadeWeights"][number]>["accessMultipliers"]
    | undefined {
    if (!am?.multipliers?.length) return am;
    return {
        azimuth: am.azimuth,
        tilt: am.tilt,
        multipliers: am.multipliers.map((m) => ({
            access: m.access,
            multiplier: m.multiplier,
        })) as typeof am.multipliers,
    };
}

/**
 * Strip site snapshot / thumbnail fields and any data-URL image strings from a scenario
 * before JSON is embedded in a Gemini text prompt (avoids huge base64 payloads).
 */
function omitScenarioMediaForGeminiPrompt(scenario: ModuleFillingScenarioInput): ModuleFillingScenarioInput {
    const row: Record<string, unknown> = { ...(scenario as unknown as Record<string, unknown>) };
    const stripKeys = [
        "snapshotUrl",
        "mainSnapshotUrl",
        "layoutSnapshotUrl",
        "thumbnailUrl",
        "imageUrl",
        "imageData",
    ];
    for (const k of stripKeys) {
        delete row[k];
    }
    for (const k of Object.keys(row)) {
        const v = row[k];
        if (typeof v === "string" && /^data:image\//i.test(v)) {
            delete row[k];
        }
    }
    return row as ModuleFillingScenarioInput;
}

function compactScenarioForPrompt(
    scenario: ModuleFillingScenarioInput,
    scenarioIndex: number,
): ModuleFillingScenarioInput {
    const base = omitScenarioMediaForGeminiPrompt(scenario);
    const includePointDetail = scenarioIndex === 0;
    const shadeWeights = (base.shadeWeights ?? []).map((roof) => ({
        roofFaceId: roof.roofFaceId,
        roofNum: roof.roofNum,
        solarAccess: roof.solarAccess,
        roofMultiplier: roof.roofMultiplier,
        moduleCount: roof.moduleCount,
        actualProduction: roof.actualProduction,
        ptHashAverage: (roof as { ptHashAverage?: number }).ptHashAverage,
        shadePoints: includePointDetail
            ? compactShadePointsForPrompt(roof.shadePoints, MAX_SHADE_POINTS_PER_ROOF_IN_PROMPT)
            : undefined,
        accessMultipliers: compactAccessMultipliersForPrompt(roof.accessMultipliers),
    }));

    // Omit layout sketch / drawing-context fields from the Gemini payload (large JSON vertices, etc.).
    return {
        ...base,
        drawingSummary: undefined,
        setbackSummary: undefined,
        pathwaySummary: undefined,
        shadeWeights,
    };
}

/** When there are many tree scenarios, keep baseline plus a capped set (prefer multi-tree rows, then single-tree). */
function limitScenarioCountForPrompt(scenarios: ModuleFillingScenarioInput[]): ModuleFillingScenarioInput[] {
    if (scenarios.length === 0) return scenarios;
    if (scenarios.length <= MAX_SCENARIOS_IN_PROMPT) return scenarios;
    const out: ModuleFillingScenarioInput[] = [scenarios[0]];
    const rest = scenarios.slice(1);
    const budget = MAX_SCENARIOS_IN_PROMPT - 1;
    const multi = rest.filter((s) => (s.treeIds?.length ?? 0) > 1);
    const single = rest.filter((s) => (s.treeIds?.length ?? 0) <= 1);
    const multiTake = multi.slice(0, Math.min(multi.length, Math.floor(budget / 2)));
    const singleBudget = budget - multiTake.length;
    const singleTake = single.slice(0, Math.max(0, singleBudget));
    out.push(...multiTake, ...singleTake);
    return out;
}

const MODULE_FILLING_SYSTEM_PROMPT = `You are an expert solar design assistant. You receive data from a roof/site editor: per-roof summary (solar access, multiplier, moduleCount = number of modules on that roof in the current layout) and per-shade-point weights (ptHashVal = solar access 0-100 for each point). Each scenario includes treeMultipliers (only trees with non-zero treeImpactMulti) and actualWattage (total system production in watts for that layout). The layout is fixed: moduleCount per roof is the same in every scenario; only shade and production change when trees are ghosted.

Tree impact convention: treeMultipliers omits any tree whose treeImpactMulti is 0. If a tree is mentioned elsewhere (e.g. treeMetaSummary or scenario labels) but does not appear in treeMultipliers, treat its treeImpactMulti as 0 for that scenario—do not assume it materially shades the array or rank it with high-impact trees unless it appears in treeMultipliers with a positive value.

Production model: actualWattage is the total system AC production for the full system. Internally, the app:
- Computes sun position using the NREL Solar Position Algorithm (SPA) and derives irradiance / solar-access values per shade point.
- Aggregates shade points into per-module solar access (irrPro 0-100) and per-roof solar access.
- Looks up a per-roof production multiplier (roofMultiplier) from NREL-based access multipliers using roof tilt, azimuth, and solar access.
- Computes DC array size per roof as arraySizeDc = moduleCount × modulePower (W per module).
- Computes per-roof production as roofProduction = moduleCount × modulePower × roofMultiplier.
- Computes system-level actualWattage as the sum of roofProduction over all roofs.
- Computes TSRS/TSRF-style efficiency as efficiency = roofMultiplier / idealMultiplier, where idealMultiplier comes from idealMonthly (NREL-based) values for the same tilt.

For reasoning, you can treat roofMultiplier as an NREL/PV-multiplier that already encodes the effects of tilt, azimuth, and shading, and actualWattage as the ground-truth production from this model.

When present, each roof's accessMultipliers field contains:
- azimuth, tilt: the roof orientation the multipliers were computed for.
- multipliers: an array of { access, multiplier, monthlyMultipliers }, where "access" is a solar-access band (0-100), "multiplier" is the NREL-based production multiplier for that band, and "monthlyMultipliers" contains per-month multipliers for that band.
Use these to better understand how production changes as solar access improves or worsens on that roof, and to reason about which roofs/trees have the highest upside when shade is reduced.

In addition to shade and production data, some scenarios may also include:
- ahjRulesSummary: text describing local AHJ rules, fire-code requirements, and any special constraints.
- moduleTypeSummary: module manufacturer/model, power rating, and any relevant electrical/mechanical metadata (e.g., 400 W monofacial, portrait, rail-based).
- treeMetaSummary: per-tree metadata such as height, crown diameter, trunk radius, and type/species, when available.

When making recommendations, respect AHJ rules described in ahjRulesSummary. Use treeMetaSummary where present to reason about which trees are more or less favorable for cutting. Geometry/setback/pathway detail is not included in the input JSON—rely on shade weights, production, and AHJ text.

Your task: recommend the best roofs and specific shade points (by roof and point index) with high solar access for placing panels, and the best combinations of tree cutting vs cost to improve production. Avoid shade points with ptHashVal < 50 (low solar access). Do not recommend roofs where adding modules is not worth it (e.g. very low solar access or fewer than 2 modules). Be concise. Structure your reply as:
1. Best roofs for panels (by roofFaceId/roofNum) and, within each roof, which shade points (index and ptHashVal) have the highest solar access—prioritize ptHashVal >= 85, and exclude or deprioritize points with ptHashVal < 50.
2. For each scenario (base vs cutting each tree): which roofs and shade points improve most; suggest specific shade point indices when relevant.
3. Tree-cutting recommendations: which trees are worth cutting (production gain vs cut cost) and in what order. Recommend single-tree and multi-tree combinations (scenarios with treeIds array have actual production for cutting those trees together) when they increase production and cost is worth it.
4. Any caveats (e.g. small gains, high cost).

Analyse which combinations of roofs produce the best efficiency, best solar access, and best production. You MUST build exactly four layout combinations, using these category names:
- "Max Production with Best Roofs" — fill only the best roofs for maximum total production (same logic as oneDRAW best-roofs fill; exclude low solar access).
- "Best Solar Access" — roofs/points with highest solar access; prefer quality over quantity.
- "Balanced" — trade-off between production and efficiency; may include roofs with moderate solar access (e.g. 50–60%) if they add meaningful production.
- "Maximum Efficiency" — prefer fewer, higher-efficiency panels (exclude modules with efficiency < 60%); quality over quantity.

Each combination MUST be a meaningfully different set of roofs optimised for that category:
- Do NOT assign the exact same roofFaceId list to more than one of the four combinations.
- \"Balanced\" should, whenever possible, include at least one roof that is NOT in \"Max Production with Best Roofs\" if there exists any roof with solar access or ptHashAverage in the 50–60 range that adds useful production.
- \"Maximum Efficiency\" should drop roofs that rely heavily on low-efficiency modules (< 60%) even if they appear in other combinations; it may use a strict subset of high-quality roofs.

Do not include roofs that would fit fewer than 2 modules or have very low solar access. Prefer roofs with many modules, high solar access and good multipliers; avoid low-production outliers. For Balanced vs Maximum Efficiency: Balanced can include more roofs/panels with 50–60% efficiency; Maximum Efficiency should favour roofs where panels stay above 60% efficiency.

Important: End your response with these two lines (use actual roofFaceIds from the input):
RECOMMENDED_ROOF_IDS: roofFaceId1,roofFaceId2,... (your single best set)
RECOMMENDED_ROOF_COMBINATIONS: Max Production with Best Roofs|id1,id2; Best Solar Access|id3,id4; Balanced|id5,id6; Maximum Efficiency|id7,id8; Highest Multiplier|id9,id10 (exactly these five names, in any order; name, then pipe, then comma-separated roofFaceIds; semicolon between combinations).`;

/**
 * Build the user prompt containing scenario data (base + single-tree + multi-tree with actual production).
 * Optionally includes site latitude/longitude and a short description of the solar model for Gemini.
 * @param layoutVariantsSummary - Optional summary of pre-simulated layout variants (portrait/landscape, 50%/60% efficiency) to help differentiate Balanced vs Max Efficiency.
 */
export function buildModuleFillingPrompt(
    scenarios: ModuleFillingScenarioInput[],
    latitude?: number,
    longitude?: number,
    siteAddress?: string | null,
    layoutVariantsSummary?: string | null
): string {
    const trimmedScenarios = limitScenarioCountForPrompt(scenarios);
    const forPrompt = trimmedScenarios.map((s, i) => compactScenarioForPrompt(s, i));

    // Many fields are identical across scenarios (base + each tree-cut). Avoid repeating them to save tokens.
    // We treat the first scenario as the "global" source of truth, and omit identical copies from each scenario row.
    const globalFromFirst = forPrompt[0]
        ? {
              ahjRulesSummary: forPrompt[0].ahjRulesSummary,
              moduleTypeSummary: forPrompt[0].moduleTypeSummary,
              treeMetaSummary: forPrompt[0].treeMetaSummary,
          }
        : null;

    const scenarioRows = forPrompt.map((s) => {
        if (!globalFromFirst) return s;
        const isSameAhj = s.ahjRulesSummary === globalFromFirst.ahjRulesSummary;
        const isSameModuleType = s.moduleTypeSummary === globalFromFirst.moduleTypeSummary;
        const isSameTreeMeta = s.treeMetaSummary === globalFromFirst.treeMetaSummary;

        // Preserve data if it differs per scenario; otherwise omit and let Gemini read from GLOBAL_CONTEXT.
        // (undefined fields are not emitted by JSON.stringify)
        return {
            ...s,
            ahjRulesSummary: isSameAhj ? undefined : s.ahjRulesSummary,
            moduleTypeSummary: isSameModuleType ? undefined : s.moduleTypeSummary,
            treeMetaSummary: isSameTreeMeta ? undefined : s.treeMetaSummary,
        };
    });

    const json = JSON.stringify(
        { GLOBAL_CONTEXT: globalFromFirst ?? undefined, SCENARIOS: scenarioRows, }, null, 2,
    );
    const hasLocation =
        typeof latitude === "number" && Number.isFinite(latitude) &&
        typeof longitude === "number" && Number.isFinite(longitude);
    const locationLines: string[] = [];
    if (hasLocation) {
        locationLines.push(`- Latitude: ${latitude!.toFixed(6)}°`);
        locationLines.push(`- Longitude: ${longitude!.toFixed(6)}°`);
    }
    if (siteAddress && siteAddress.trim()) {
        locationLines.push(`- Site address: ${siteAddress.trim()}`);
    }
    const locationBlock = locationLines.length
        ? `Site location and solar model:
            ${locationLines.join("\n")}
            - Sun position is computed with the NREL Solar Position Algorithm (SPA).
            - NREL / irradiance-based access multipliers are used to derive roofMultiplier and idealMultiplier per roof (by tilt, azimuth, and solar access).`
        : "";

    // Pull AHJ / setback notes from the first scenario (all scenarios share them).
    const ahjSummary = forPrompt[0]?.ahjRulesSummary;
    const ahjBlock = ahjSummary ? `AHJ rules and setback notes: ${ahjSummary}` : "";
    const variantsRaw = layoutVariantsSummary?.trim() ?? "";
    const variantsTruncated = variantsRaw
        ? truncateText(variantsRaw, MAX_LAYOUT_VARIANTS_SUMMARY_CHARS, "layoutVariantsSummary") ?? ""
        : "";
    const variantsBlock = variantsTruncated
        ? `Layout variants (pre-simulated on all roofs to help differentiate strategies; use to recommend different roof sets for Balanced vs Maximum Efficiency):${variantsTruncated}`
        : "";
    return `${MODULE_FILLING_SYSTEM_PROMPT}
        ${locationBlock}
        ${ahjBlock}
        Important: The input JSON may include a GLOBAL_CONTEXT object. Treat it as the single source of truth for shared fields that apply to ALL scenarios (e.g., ahjRulesSummary, moduleTypeSummary, treeMetaSummary). Individual scenario rows may omit those fields when they match GLOBAL_CONTEXT.
        Input data (scenarios: base + one per single-tree cut + multi-tree combinations that were worth it as single cuts; multi-tree entries have treeIds array and actual production). In each scenario, treeMultipliers lists only trees with treeImpactMulti ≠ 0; any other site tree has treeImpactMulti = 0 for analysis.
        Token budget: Only the first (baseline) scenario includes shadePoints per roof, as a small sample (highest-access, lowest-access, and spread by index). Other scenarios include roof-level solarAccess, roofMultiplier, actualProduction, and moduleCount only. If SCENARIOS is shorter than all runs on site, multi-tree rows were prioritized, then single-tree.
        Estimated production per roof: (moduleCount × roofMultiplier) / sum over all roofs(moduleCount × roofMultiplier) × scenario actualWattage. Each roof's moduleCount is the number of modules on that roof in the current layout. Shade points with ptHashVal < 50 should be avoided for module placement; prefer roofs and points with ptHashVal ≥ 50.
        ${json}
        ${variantsBlock}
        Provide your recommendation based on this data, including single-tree and multi-tree cut options when worth it. Use the layout variants (if present) to suggest different roof combinations for Balanced (e.g. more panels, 50% threshold) vs Maximum Efficiency (fewer, higher-quality panels, 60% threshold), and a distinct combination for Highest Multiplier (focus on highest-efficiency / multiplier roofs). End with RECOMMENDED_ROOF_IDS: (single best set) and RECOMMENDED_ROOF_COMBINATIONS exactly as specified above (five named combinations).`;
}

/**
 * Call Gemini for module-fill recommendation. Uses plain text response (no JSON).
 */
export async function getModuleFillingRecommendation(
    apiKey: string,
    scenarios: ModuleFillingScenarioInput[],
    latitude?: number,
    longitude?: number,
    siteAddress?: string | null,
    layoutVariantsSummary?: string | null
): Promise<string> {
    const prompt = buildModuleFillingPrompt(scenarios, latitude, longitude, siteAddress ?? undefined, layoutVariantsSummary ?? undefined);
    return callGemini(prompt, GEMINI_MODEL_ID, apiKey);
}

/** Summary of a layout option for ideal-layout prediction */
export interface LayoutOptionSummary {
    name: string;
    productionW: number;
    modules: number;
}

const IDEAL_LAYOUT_PROMPT = `You are a solar design advisor. You are given a list of layout options for a solar installation, each with estimated production (W) and module count. Your task is to recommend the ideal layout and briefly explain why (consider production, module count, and balance). Reply in 2-4 short sentences. Do not use markdown headers.`;

/**
 * Ask Gemini to recommend the ideal layout from the given options (production + modules per layout).
 */
export async function getIdealLayoutRecommendation(
    apiKey: string,
    layouts: LayoutOptionSummary[]
): Promise<string> {
    const lines = layouts.map((l) => `- ${l.name}: ${Math.round(l.productionW).toLocaleString()} W, ${l.modules} modules`).join("\n");
    const prompt = `${IDEAL_LAYOUT_PROMPT}\n\nLayout options:\n${lines}\n\nYour recommendation:`;
    return callGemini(prompt, GEMINI_MODEL_ID, apiKey);
}

interface SaveAIResultPayload {
    layoutSnapshots?: string;
    variantSnapshots?: string;
    aiScenarios: string;
    treeCuttingScenarios: string;
    sales_Force_Id?: string;
}

/** Large AI save payloads (snapshots) can take several minutes end-to-end. */
const SAVE_AI_RESULT_TIMEOUT_MS = 300_000;

export async function saveAIResult(data: SaveAIResultPayload, onProgress?: (progress: number) => void) { // aiResult: string, scenarios: ModuleFillingScenarioInput[]
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), SAVE_AI_RESULT_TIMEOUT_MS);
    try {
        const payload = { ...data, sales_Force_Id: getSalesforceIdForAiCache() ?? undefined };
        const res = await bearerAxios.post(
            import.meta.env.VITE_BASE_URL + import.meta.env.VITE_SAVE_AI_RESULT_URL!,
            payload,
            {
                headers: { "Content-Type": "application/json" },
                signal: controller.signal,
                onUploadProgress: (progressEvent: any) => {
                    const total = progressEvent.total;
                    if (!total || !Number.isFinite(total) || total <= 0) return;
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / total);
                    // Upload can report 100% before the server finishes processing the response.
                    onProgress?.(Math.min(99, percentCompleted));
                },
            },
        );
        onProgress?.(100);
        invalidateAiResultSessionCache();
        return res.data;
    } catch (error) {
        if (controller.signal.aborted) {
            throw new Error(`Save AI result timed out after ${SAVE_AI_RESULT_TIMEOUT_MS / 1000}s`);
        }
        logger.error(error);
        throw error;
    } finally {
        window.clearTimeout(timeoutId);
    }
}

export async function saveWishlist(wishlist: SavedAIDesign[]) {
    try {
        const payload = {
            aiWishlist: JSON.stringify(wishlist),
            sales_Force_Id: getSalesforceIdForAiCache() ?? undefined,
        };
        const res = await bearerAxios.post(
            import.meta.env.VITE_BASE_URL + import.meta.env.VITE_SAVE_AI_RESULT_URL!,
            payload,
            { headers: { "Content-Type": "application/json" }},
        );
        invalidateAiResultSessionCache();
        return res.data;
    } catch (error) {
        logger.error(error);
    }
    return null;
}

type FetchAIResultPayload = {
    layoutSnapshots: Record<string, unknown> | null;
    variantSnapshots: unknown;
    aiScenarios: unknown;
    treeCuttingScenarios: unknown;
    aiWishlist: unknown;
    aiResult?: string | null;
};
export type LocalAiResultCachePayload = FetchAIResultPayload;

const AI_RESULT_FETCH_CACHE_STORAGE_VERSION = 1;
const AI_RESULT_FETCH_INDEXED_DB_NAME = 'onedraw-ai-result-cache';
const AI_RESULT_FETCH_INDEXED_DB_VERSION = 2;
const AI_RESULT_FETCH_ENTRIES_STORE = 'aiResultFetchEntries';
const AI_RESULT_FETCH_META_STORE = 'aiResultFetchMeta';
const AI_RESULT_LOCAL_STORE = 'aiResultLocalCache';
const AI_RESULT_LOCAL_CACHE_VERSION = 1;
const AI_RESULT_ACTIVE_TABS_LS_KEY = `onedraw-ai-active-tabs|v${AI_RESULT_FETCH_CACHE_STORAGE_VERSION}`;
const TAB_SESSION_ID_KEY = 'onedraw-ai-tab-session-id';

/**
 * Single source of truth for which Salesforce id keys AI fetch cache + localStorage persistence.
 * When the URL carries `?sfid=` (configure refresh / deep link), prefer it over sessionStorage
 * so IndexedDB hydration matches the page. When the URL omits sfid (in-app navigation), keep
 * using sessionStorage so the same opportunity bucket is preserved.
 */
export function getSalesforceIdForAiCache(): string | null {
    if (typeof window === 'undefined') return null;
    const url = (new URLSearchParams(window.location.search).get('sfid') || '').trim();
    const ss = (sessionStorage.getItem('SalesForceId') || '').trim();
    return url || ss || null;
}

/** Per–Salesforce-id generation: bump when AI is re-run or saved so GET cache is skipped for that opportunity. */
const aiResultGenerationBySfid = new Map<string, number>();

const aiResultSessionCache = new Map<string, { generation: number; payload: FetchAIResultPayload }>();

const hydratedSfidKeys = new Set<string>();
const hydrationPromiseBySfidKey = new Map<string, Promise<void>>();
let aiResultFetchDbPromise: Promise<IDBDatabase | null> | null = null;
let tabLifecycleInstalled = false;
let registeredSfidForTab: string | null = null;

function getOrCreateTabSessionId(): string {
    if (typeof window === 'undefined') return 'ssr';
    let id = sessionStorage.getItem(TAB_SESSION_ID_KEY);
    if (!id) {
        id =
            typeof crypto !== 'undefined' && 'randomUUID' in crypto
                ? crypto.randomUUID()
                : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem(TAB_SESSION_ID_KEY, id);
    }
    return id;
}

function readActiveTabRegistry(): Record<string, string[]> {
    try {
        const raw = localStorage.getItem(AI_RESULT_ACTIVE_TABS_LS_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as Record<string, string[]>;
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function writeActiveTabRegistry(registry: Record<string, string[]>): void {
    try {
        localStorage.setItem(AI_RESULT_ACTIVE_TABS_LS_KEY, JSON.stringify(registry));
    } catch (e) {
        logger.warn('Could not update AI cache active tab registry', e);
    }
}

function registerSfidTab(sfId: string): void {
    const sk = sfidStorageKey(sfId);
    const tabId = getOrCreateTabSessionId();
    const registry = readActiveTabRegistry();
    const tabs = registry[sk] ?? [];
    if (!tabs.includes(tabId)) {
        registry[sk] = [...tabs, tabId];
        writeActiveTabRegistry(registry);
    }
}

/** Remove this tab from the sfid registry. Returns true when no tabs remain for that sfid. */
function unregisterSfidTab(sfId: string): boolean {
    const sk = sfidStorageKey(sfId);
    const tabId = getOrCreateTabSessionId();
    const registry = readActiveTabRegistry();
    const tabs = registry[sk] ?? [];
    if (!tabs.includes(tabId)) return tabs.length === 0;

    const next = tabs.filter((id) => id !== tabId);
    if (next.length === 0) {
        delete registry[sk];
        writeActiveTabRegistry(registry);
        return true;
    }
    registry[sk] = next;
    writeActiveTabRegistry(registry);
    return false;
}

function isReloadPageHide(): boolean {
    try {
        const legacyNav = (performance as Performance & { navigation?: { type?: number } }).navigation;
        if (legacyNav?.type === 1) return true;
        const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        if (nav?.type === 'reload') return true;
    } catch {
        // ignore
    }
    return false;
}

function sfidStorageKey(sfId: string | null): string {
    return sfId ?? '__no_sfid__';
}

function getAiResultCacheGeneration(sfId: string | null): number {
    return aiResultGenerationBySfid.get(sfidStorageKey(sfId)) ?? 0;
}

function bumpAiResultCacheGeneration(sfId: string | null): void {
    const k = sfidStorageKey(sfId);
    aiResultGenerationBySfid.set(k, (aiResultGenerationBySfid.get(k) ?? 0) + 1);
}

function purgeMemoryCacheForSfid(sfId: string | null): void {
    const prefix = `${sfidStorageKey(sfId)}|`;
    for (const mk of [...aiResultSessionCache.keys()]) {
        if (mk.startsWith(prefix)) {
            aiResultSessionCache.delete(mk);
        }
    }
}

function rawOpenAiResultFetchDb(): Promise<IDBDatabase | null> {
    return new Promise((resolve) => {
        try {
            const req = indexedDB.open(AI_RESULT_FETCH_INDEXED_DB_NAME, AI_RESULT_FETCH_INDEXED_DB_VERSION);
            req.onupgradeneeded = () => {
                const db = req.result;
                if (!db.objectStoreNames.contains(AI_RESULT_FETCH_ENTRIES_STORE)) {
                    const entries = db.createObjectStore(AI_RESULT_FETCH_ENTRIES_STORE, { keyPath: 'cacheKey' });
                    entries.createIndex('sfidKey_generation', ['sfidKey', 'generation'], { unique: false });
                    entries.createIndex('sfidKey', 'sfidKey', { unique: false });
                }
                if (!db.objectStoreNames.contains(AI_RESULT_FETCH_META_STORE)) {
                    db.createObjectStore(AI_RESULT_FETCH_META_STORE, { keyPath: 'metaKey' });
                }
                if (!db.objectStoreNames.contains(AI_RESULT_LOCAL_STORE)) {
                    db.createObjectStore(AI_RESULT_LOCAL_STORE, { keyPath: 'cacheKey' });
                }
            };
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => {
                logger.warn('Could not open IndexedDB for AI result cache', req.error);
                resolve(null);
            };
        } catch (e) {
            logger.warn('IndexedDB initialization failed for AI result cache', e);
            resolve(null);
        }
    });
}

function openAiResultFetchDb(): Promise<IDBDatabase | null> {
    if (typeof window === 'undefined' || typeof indexedDB === 'undefined') {
        return Promise.resolve(null);
    }
    if (aiResultFetchDbPromise) {
        return aiResultFetchDbPromise;
    }
    aiResultFetchDbPromise = rawOpenAiResultFetchDb();
    return aiResultFetchDbPromise;
}

function localAiCacheKeyForSfid(sfId: string | null): string {
    return `local|v${AI_RESULT_LOCAL_CACHE_VERSION}|${sfidStorageKey(sfId)}`;
}

function normalizeAiScenariosForCacheCheck(raw: unknown): Record<string, unknown> {
    if (raw == null) return {};
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw) as unknown;
            return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
        } catch {
            return {};
        }
    }
    if (typeof raw === 'object') return raw as Record<string, unknown>;
    return {};
}

/** True when a cached aiScenarios blob has layout catalog rows worth persisting. */
function localCachePayloadHasCatalog(aiScenarios: unknown): boolean {
    const blob = normalizeAiScenariosForCacheCheck(aiScenarios);
    const production = (blob.layoutProductionByKey ?? {}) as Record<string, number>;
    if (Object.values(production).some((v) => typeof v === 'number' && Number.isFinite(v) && v > 0)) {
        return true;
    }
    const modules = (blob.layoutModulesByKey ?? {}) as Record<string, number>;
    if (Object.values(modules).some((v) => typeof v === 'number' && Number.isFinite(v) && v > 0)) {
        return true;
    }
    const variants = (blob.aiCategoryLayoutVariants ?? {}) as Record<string, unknown[]>;
    if (Object.keys(variants).length > 0) return true;
    const mains = (blob.categoryMainLayoutByCategory ?? {}) as Record<string, unknown>;
    if (Object.keys(mains).length > 0) return true;
    const scenarios = blob.aiModuleFillingScenarios;
    return Array.isArray(scenarios) && scenarios.length > 0;
}

export type SaveLocalAiResultCacheOptions = {
    /** When true, always write (e.g. immediately after a completed AI run). */
    force?: boolean;
};

export async function saveLocalAiResultCache(
    payload: LocalAiResultCachePayload,
    sfId?: string | null,
    options?: SaveLocalAiResultCacheOptions,
): Promise<void> {
    const resolvedSfid = sfId ?? getSalesforceIdForAiCache();
    const incomingHasCatalog = localCachePayloadHasCatalog(payload.aiScenarios);

    if (!options?.force) {
        const existing = await loadLocalAiResultCache(resolvedSfid);
        const existingHasCatalog = localCachePayloadHasCatalog(existing?.aiScenarios);
        if (existingHasCatalog && !incomingHasCatalog) {
            return;
        }
        if (!incomingHasCatalog && !existingHasCatalog) {
            return;
        }
    }

    const db = await openAiResultFetchDb();
    if (!db) return;
    await new Promise<void>((resolve) => {
        try {
            const tx = db.transaction(AI_RESULT_LOCAL_STORE, 'readwrite');
            const store = tx.objectStore(AI_RESULT_LOCAL_STORE);
            const row = {
                cacheKey: localAiCacheKeyForSfid(resolvedSfid),
                sfidKey: sfidStorageKey(resolvedSfid),
                updatedAt: Date.now(),
                payload,
            };
            store.put(row);
            tx.oncomplete = () => resolve();
            tx.onerror = () => {
                logger.warn('Could not persist local AI result cache to IndexedDB', tx.error);
                resolve();
            };
            tx.onabort = () => resolve();
        } catch (e) {
            logger.warn('Could not write local AI result cache to IndexedDB', e);
            resolve();
        }
    });
}

export async function loadLocalAiResultCache(sfId?: string | null): Promise<LocalAiResultCachePayload | null> {
    const resolvedSfid = sfId ?? getSalesforceIdForAiCache();
    const db = await openAiResultFetchDb();
    if (!db) return null;
    return new Promise<LocalAiResultCachePayload | null>((resolve) => {
        try {
            const tx = db.transaction(AI_RESULT_LOCAL_STORE, 'readonly');
            const store = tx.objectStore(AI_RESULT_LOCAL_STORE);
            const req = store.get(localAiCacheKeyForSfid(resolvedSfid));
            req.onsuccess = () => {
                const direct = req.result?.payload;
                resolve(direct && typeof direct === 'object' ? (direct as LocalAiResultCachePayload) : null);
            };
            req.onerror = () => resolve(null);
        } catch {
            resolve(null);
        }
    });
}

/** Try several SFID keys — URL param and sessionStorage can disagree after in-app navigation. */
export async function loadLocalAiResultCacheWithFallback(
    sfId?: string | null,
    extraSfIds?: Array<string | null | undefined>,
): Promise<LocalAiResultCachePayload | null> {
    const candidates: string[] = [];
    const add = (value?: string | null) => {
        const trimmed = (value ?? '').trim();
        if (trimmed && !candidates.includes(trimmed)) candidates.push(trimmed);
    };
    add(sfId ?? undefined);
    add(getSalesforceIdForAiCache() ?? undefined);
    if (typeof window !== 'undefined') {
        add(sessionStorage.getItem('SalesForceId'));
        add(new URLSearchParams(window.location.search).get('sfid'));
    }
    for (const extra of extraSfIds ?? []) add(extra ?? undefined);

    for (const id of candidates) {
        const hit = await loadLocalAiResultCache(id);
        if (hit?.aiScenarios != null) return hit;
    }
    return null;
}

async function writeAiResultGenerationToDb(sfId: string | null): Promise<void> {
    const db = await openAiResultFetchDb();
    if (!db) return;
    const sk = sfidStorageKey(sfId);
    const generation = getAiResultCacheGeneration(sfId);
    await new Promise<void>((resolve) => {
        try {
            const tx = db.transaction(AI_RESULT_FETCH_META_STORE, 'readwrite');
            tx.objectStore(AI_RESULT_FETCH_META_STORE).put({
                metaKey: `gen|${sk}|v${AI_RESULT_FETCH_CACHE_STORAGE_VERSION}`,
                generation,
            });
            tx.oncomplete = () => resolve();
            tx.onerror = () => {
                logger.warn('Could not persist AI cache generation to IndexedDB', tx.error);
                resolve();
            };
            tx.onabort = () => resolve();
        } catch (e) {
            logger.warn('Could not write AI cache generation to IndexedDB', e);
            resolve();
        }
    });
}

async function upsertAiResultEntryInDb(
    sfId: string | null,
    cacheKey: string,
    generation: number,
    payload: FetchAIResultPayload,
): Promise<void> {
    const db = await openAiResultFetchDb();
    if (!db) return;
    const sk = sfidStorageKey(sfId);
    await new Promise<void>((resolve) => {
        try {
            const tx = db.transaction(AI_RESULT_FETCH_ENTRIES_STORE, 'readwrite');
            tx.objectStore(AI_RESULT_FETCH_ENTRIES_STORE).put({
                cacheKey,
                sfidKey: sk,
                generation,
                payload,
            });
            tx.oncomplete = () => resolve();
            tx.onerror = () => {
                logger.warn('Could not persist AI cache entry to IndexedDB', tx.error);
                resolve();
            };
            tx.onabort = () => resolve();
        } catch (e) {
            logger.warn('Could not write AI cache entry to IndexedDB', e);
            resolve();
        }
    });
}

async function clearAiResultEntriesForSfidInDb(sfId: string | null): Promise<void> {
    const db = await openAiResultFetchDb();
    if (!db) return;
    const sk = sfidStorageKey(sfId);
    await new Promise<void>((resolve) => {
        try {
            const tx = db.transaction(AI_RESULT_FETCH_ENTRIES_STORE, 'readwrite');
            const store = tx.objectStore(AI_RESULT_FETCH_ENTRIES_STORE);
            const index = store.index('sfidKey');
            const range = IDBKeyRange.only(sk);
            const cursorReq = index.openCursor(range);
            cursorReq.onsuccess = () => {
                const cursor = cursorReq.result;
                if (!cursor) return;
                cursor.delete();
                cursor.continue();
            };
            tx.oncomplete = () => resolve();
            tx.onerror = () => {
                logger.warn('Could not clear AI cache entries from IndexedDB', tx.error);
                resolve();
            };
            tx.onabort = () => resolve();
        } catch (e) {
            logger.warn('Could not clear AI cache entries from IndexedDB', e);
            resolve();
        }
    });
}

async function hydrateAiResultCacheFromStorage(sfId: string | null): Promise<void> {
    const sk = sfidStorageKey(sfId);
    if (hydratedSfidKeys.has(sk)) return;

    const inFlight = hydrationPromiseBySfidKey.get(sk);
    if (inFlight) {
        await inFlight;
        return;
    }

    const hydratePromise = (async () => {
        const db = await openAiResultFetchDb();
        if (!db) return;
        try {
            const generation = await new Promise<number>((resolve) => {
                const tx = db.transaction(AI_RESULT_FETCH_META_STORE, 'readonly');
                const req = tx
                    .objectStore(AI_RESULT_FETCH_META_STORE)
                    .get(`gen|${sk}|v${AI_RESULT_FETCH_CACHE_STORAGE_VERSION}`);
                req.onsuccess = () => {
                    const g = req.result?.generation;
                    resolve(typeof g === 'number' ? g : 0);
                };
                req.onerror = () => resolve(0);
            });

            aiResultGenerationBySfid.set(sk, generation);
            const entries = await new Promise<Array<{ cacheKey: string; generation: number; payload?: FetchAIResultPayload }>>(
                (resolve) => {
                    const tx = db.transaction(AI_RESULT_FETCH_ENTRIES_STORE, 'readonly');
                    const store = tx.objectStore(AI_RESULT_FETCH_ENTRIES_STORE);
                    const index = store.index('sfidKey_generation');
                    const req = index.getAll(IDBKeyRange.only([sk, generation]));
                    req.onsuccess = () => resolve((req.result ?? []) as Array<{ cacheKey: string; generation: number; payload?: FetchAIResultPayload }>);
                    req.onerror = () => resolve([]);
                },
            );

            for (const entry of entries) {
                if (entry?.cacheKey && entry?.payload && typeof entry.payload === 'object') {
                    aiResultSessionCache.set(entry.cacheKey, {
                        generation: entry.generation,
                        payload: entry.payload,
                    });
                }
            }
        } catch (e) {
            logger.warn('Failed to hydrate AI result cache from IndexedDB', e);
        } finally {
            hydratedSfidKeys.add(sk);
        }
    })();

    hydrationPromiseBySfidKey.set(sk, hydratePromise);
    try {
        await hydratePromise;
    } finally {
        hydrationPromiseBySfidKey.delete(sk);
    }
}

async function clearLocalAiResultCacheForSfidInDb(sfId: string | null): Promise<void> {
    const db = await openAiResultFetchDb();
    if (!db) return;
    await new Promise<void>((resolve) => {
        try {
            const tx = db.transaction(AI_RESULT_LOCAL_STORE, 'readwrite');
            tx.objectStore(AI_RESULT_LOCAL_STORE).delete(localAiCacheKeyForSfid(sfId));
            tx.oncomplete = () => resolve();
            tx.onerror = () => {
                logger.warn('Could not clear local AI result cache from IndexedDB', tx.error);
                resolve();
            };
            tx.onabort = () => resolve();
        } catch (e) {
            logger.warn('Could not clear local AI result cache from IndexedDB', e);
            resolve();
        }
    });
}

async function clearAiResultMetaForSfidInDb(sfId: string | null): Promise<void> {
    const db = await openAiResultFetchDb();
    if (!db) return;
    const sk = sfidStorageKey(sfId);
    await new Promise<void>((resolve) => {
        try {
            const tx = db.transaction(AI_RESULT_FETCH_META_STORE, 'readwrite');
            tx.objectStore(AI_RESULT_FETCH_META_STORE).delete(
                `gen|${sk}|v${AI_RESULT_FETCH_CACHE_STORAGE_VERSION}`,
            );
            tx.oncomplete = () => resolve();
            tx.onerror = () => {
                logger.warn('Could not clear AI cache meta from IndexedDB', tx.error);
                resolve();
            };
            tx.onabort = () => resolve();
        } catch (e) {
            logger.warn('Could not clear AI cache meta from IndexedDB', e);
            resolve();
        }
    });
}

/** Drop all IndexedDB + in-memory AI cache rows for one opportunity/site. */
export async function clearAiResultCacheForSfid(sfId: string | null): Promise<void> {
    if (!sfId?.trim()) return;
    const sk = sfidStorageKey(sfId);
    purgeMemoryCacheForSfid(sfId);
    hydratedSfidKeys.delete(sk);
    aiResultGenerationBySfid.delete(sk);
    await clearAiResultEntriesForSfidInDb(sfId);
    await clearLocalAiResultCacheForSfidInDb(sfId);
    await clearAiResultMetaForSfidInDb(sfId);
}

/**
 * Track which Salesforce ids this browser tab currently has open.
 * Call when the active opportunity / configure sfid changes.
 */
export function syncAiResultCacheTabRegistration(sfId?: string | null): void {
    if (typeof window === 'undefined') return;
    const resolved = (sfId ?? getSalesforceIdForAiCache())?.trim() || null;
    if (resolved === registeredSfidForTab) return;
    if (registeredSfidForTab) {
        unregisterSfidTab(registeredSfidForTab);
    }
    registeredSfidForTab = resolved;
    if (resolved) registerSfidTab(resolved);
}

/**
 * On tab close, clear IndexedDB only for the current site's sfid when no other tab
 * still has that site open. Refresh keeps cache; other tabs for other sites are untouched.
 */
export function installAiResultCacheTabLifecycle(): void {
    if (tabLifecycleInstalled || typeof window === 'undefined') return;
    tabLifecycleInstalled = true;

    syncAiResultCacheTabRegistration();

    window.addEventListener('pagehide', (event) => {
        if (event.persisted) return;
        if (isReloadPageHide()) return;

        const sfid = registeredSfidForTab ?? getSalesforceIdForAiCache();
        registeredSfidForTab = null;
        if (!sfid?.trim()) return;

        const isLastTab = unregisterSfidTab(sfid);
        if (isLastTab) {
            void clearAiResultCacheForSfid(sfid);
        }
    });

    window.addEventListener('pageshow', (event) => {
        if (!event.persisted) {
            syncAiResultCacheTabRegistration();
        }
    });
}

/**
 * Bump cache generation for the current opportunity and clear cached GETs so the next `fetchAIResult`
 * hits the API. Call when the user starts a new AI run or after saving AI / wishlist data.
 */
export function invalidateAiResultSessionCache(): void {
    const sfId = typeof window !== 'undefined' ? getSalesforceIdForAiCache() : null;
    bumpAiResultCacheGeneration(sfId);
    purgeMemoryCacheForSfid(sfId);
    void writeAiResultGenerationToDb(sfId);
    void clearAiResultEntriesForSfidInDb(sfId);
}

function cacheKeyForFetchAIResult(sfId: string | null, types: AIResultType[] | undefined): string {
    const t =
        types != null && types.length > 0
            ? [...types].map(String).sort().join(',')
            : '__all__';
    return `${sfidStorageKey(sfId)}|${t}`;
}

function cloneFetchAIResultPayload(payload: FetchAIResultPayload): FetchAIResultPayload {
    try {
        return structuredClone(payload) as FetchAIResultPayload;
    } catch {
        return JSON.parse(JSON.stringify(payload)) as FetchAIResultPayload;
    }
}

function parseAiResultJsonField<T>(raw: unknown): T | null {
    if (raw == null) return null;
    if (typeof raw === 'object') return raw as T;
    if (typeof raw !== 'string') return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    try {
        return JSON.parse(trimmed) as T;
    } catch {
        return null;
    }
}

export async function fetchAIResult(
    types?: AIResultType[],
    options?: { bypassCache?: boolean },
) {
    const sfId = getSalesforceIdForAiCache();
    await hydrateAiResultCacheFromStorage(sfId);

    const key = cacheKeyForFetchAIResult(sfId, types);
    const gen = getAiResultCacheGeneration(sfId);
    if (!options?.bypassCache) {
        const hit = aiResultSessionCache.get(key);
        if (hit && hit.generation === gen) {
            return cloneFetchAIResultPayload(hit.payload);
        }
    }

    const url = import.meta.env.VITE_BASE_URL + import.meta.env.VITE_FETCH_AI_RESULT_URL! + `/${sfId}` + (types ? `?resultType=${types.join('&resultType=')}` : '');
    const res = await bearerAxios.get(
        url, { headers: { "Content-Type": "application/json" } }
    );
    const data = res.data ? res.data : { snapshots: null, Scenarios: null, TreeCuttingScenarios: null };
    const payload: FetchAIResultPayload = {
        layoutSnapshots: parseAiResultJsonField<Record<string, unknown>>(data.LayoutSnapshots),
        variantSnapshots: parseAiResultJsonField<Record<string, unknown>>(data.VariantSnapshots),
        aiScenarios: parseAiResultJsonField<unknown>(data.AiScenarios),
        treeCuttingScenarios: parseAiResultJsonField<unknown>(data.TreeCuttingScenarios),
        aiWishlist: parseAiResultJsonField<unknown>(data.AiWishlist),
    };
    aiResultSessionCache.set(key, { generation: gen, payload });
    void upsertAiResultEntryInDb(sfId, key, gen, payload);
    void writeAiResultGenerationToDb(sfId);
    return cloneFetchAIResultPayload(payload);
}

function jsonByteLength(value: unknown): number {
    try {
        return new Blob([JSON.stringify(value)]).size;
    } catch {
        return 0;
    }
}

/** In-memory AI fetch cache (Map + per-SFID generation counters). For diagnostics only. */
export function getAiResultMemoryCacheStats(): {
    sessionEntries: number;
    sfidGenerationsTracked: number;
    hydratedSfids: number;
    currentSfid: string | null;
    currentGeneration: number;
} {
    const sfid = getSalesforceIdForAiCache();
    return {
        sessionEntries: aiResultSessionCache.size,
        sfidGenerationsTracked: aiResultGenerationBySfid.size,
        hydratedSfids: hydratedSfidKeys.size,
        currentSfid: sfid,
        currentGeneration: getAiResultCacheGeneration(sfid),
    };
}

export type AiResultIndexedDbStats = {
    dbName: string;
    available: boolean;
    error?: string;
    fetchEntries: { count: number; estimatedBytes: number };
    metaRows: { count: number; estimatedBytes: number };
    localCacheRows: { count: number; estimatedBytes: number };
    totalEstimatedBytes: number;
};

/** Walk IndexedDB stores for AI result cache; byte sizes are JSON-serialized estimates. */
export async function getAiResultIndexedDbCacheStats(): Promise<AiResultIndexedDbStats> {
    const empty = (error?: string): AiResultIndexedDbStats => ({
        dbName: AI_RESULT_FETCH_INDEXED_DB_NAME,
        available: false,
        error,
        fetchEntries: { count: 0, estimatedBytes: 0 },
        metaRows: { count: 0, estimatedBytes: 0 },
        localCacheRows: { count: 0, estimatedBytes: 0 },
        totalEstimatedBytes: 0,
    });

    const db = await openAiResultFetchDb();
    if (!db) return empty("IndexedDB unavailable");

    const sumStore = (storeName: string) =>
        new Promise<{ count: number; estimatedBytes: number }>((resolve) => {
            try {
                const tx = db.transaction(storeName, "readonly");
                const store = tx.objectStore(storeName);
                let count = 0;
                let estimatedBytes = 0;
                const req = store.openCursor();
                req.onsuccess = () => {
                    const cur = req.result as IDBCursorWithValue | null;
                    if (!cur) {
                        resolve({ count, estimatedBytes });
                        return;
                    }
                    count += 1;
                    estimatedBytes += jsonByteLength(cur.value);
                    cur.continue();
                };
                req.onerror = () => resolve({ count: 0, estimatedBytes: 0 });
            } catch {
                resolve({ count: 0, estimatedBytes: 0 });
            }
        });

    const fetchEntries = await sumStore(AI_RESULT_FETCH_ENTRIES_STORE);
    const metaRows = await sumStore(AI_RESULT_FETCH_META_STORE);
    const localCacheRows = await sumStore(AI_RESULT_LOCAL_STORE);

    return {
        dbName: AI_RESULT_FETCH_INDEXED_DB_NAME,
        available: true,
        fetchEntries,
        metaRows,
        localCacheRows,
        totalEstimatedBytes:
            fetchEntries.estimatedBytes + metaRows.estimatedBytes + localCacheRows.estimatedBytes,
    };
}
